<script lang="ts">
  import Router from './components/Router.svelte';
  import Navigation from './components/Navigation.svelte';
  import ErrorBoundary from './components/ErrorBoundary.svelte';
  import ToastContainer from './components/ToastContainer.svelte';
  import HomePage from './pages/HomePage.svelte';
  import InventoryPage from './pages/InventoryPage.svelte';
  import ExecutionsPage from './pages/ExecutionsPage.svelte';
  import NodeDetailPage from './pages/NodeDetailPage.svelte';
  import IntegrationSetupPage from './pages/IntegrationSetupPage.svelte';
  import PuppetPage from './pages/PuppetPage.svelte';
  import GlobalJournalPage from './pages/GlobalJournalPage.svelte';
  import LoginPage from './pages/LoginPage.svelte';
  import RegisterPage from './pages/RegisterPage.svelte';
  import SetupPage from './pages/SetupPage.svelte';
  import UserManagementPage from './pages/UserManagementPage.svelte';
  import GroupManagementPage from './pages/GroupManagementPage.svelte';
  import GroupDetailPage from './pages/GroupDetailPage.svelte';
  import RoleManagementPage from './pages/RoleManagementPage.svelte';
  import ProvisionPage from './pages/ProvisionPage.svelte';
  import IntegrationConfigPage from './pages/IntegrationConfigPage.svelte';
  import MonitorPage from './pages/MonitorPage.svelte';
  import CrashDumpsPage from './pages/CrashDumpsPage.svelte';
  import LogsPage from './pages/LogsPage.svelte';
  import { router } from './lib/router.svelte';
  import { authManager } from './lib/auth.svelte';
  import { entraIdAuth } from './lib/entraIdAuth.svelte';
  import { get } from './lib/api';
  import { onMount } from 'svelte';

  $effect(() => {
    if (authManager.token) void authManager.refreshPermissions();
  });

  // Public pages that should render without the navigation shell
  const PUBLIC_PATHS = new Set(['/login', '/register', '/setup']);

  const routes: Record<string, any> = {
    '/': { component: HomePage, requiresAuth: true },
    '/login': LoginPage,
    '/register': RegisterPage,
    '/setup': SetupPage,
    '/inventory': { component: InventoryPage, requiresAuth: true },
    '/executions': { component: ExecutionsPage, requiresAuth: true },
    '/provision': { component: ProvisionPage, requiresAuth: true },
    '/puppet': { component: PuppetPage, requiresAuth: true },
    '/monitor': { component: MonitorPage, requiresAuth: true },
    '/journal': { component: GlobalJournalPage, requiresAuth: true },
    '/users': { component: UserManagementPage, requiresAuth: true, requiresAdmin: true },
    '/groups': { component: GroupManagementPage, requiresAuth: true, requiresAdmin: true },
    '/groups/:id': { component: GroupDetailPage, requiresAuth: true },
    '/roles': { component: RoleManagementPage, requiresAuth: true, requiresAdmin: true },
    '/nodes/:id': { component: NodeDetailPage, requiresAuth: true },
    '/integrations/:integration/setup': { component: IntegrationSetupPage, requiresAuth: true },
    '/integrations/config': { component: IntegrationConfigPage, requiresAuth: true },
    '/crash-dumps': { component: CrashDumpsPage, requiresAuth: true, requiresAdmin: true },
    '/logs': { component: LogsPage, requiresAuth: true, requiresAdmin: true }
  };

  // Detect SSO authorization code synchronously before any child mounts
  const hasSsoCode = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('code');

  let processingSso = $state(hasSsoCode);
  let setupComplete = $state(true); // Default to true to avoid flashing
  let checkingSetup = $state(true);

  onMount(async () => {
    // Exchange the SSO code first — before the router or guard can strip it
    if (hasSsoCode) {
      try {
        await entraIdAuth.handleSsoCallback();
      } finally {
        processingSso = false;
      }
    }

    // Then check setup status
    try {
      const status = await get<{ isComplete: boolean }>('/api/setup/status');
      setupComplete = status.isComplete;

      if (!setupComplete && router.currentPath !== '/setup') {
        router.navigate('/setup');
      }
    } catch (error) {
      console.error('Failed to check setup status:', error);
      setupComplete = true;
    } finally {
      checkingSetup = false;
    }
  });

  function handleError(error: Error, errorInfo: { componentStack?: string }): void {
    // Log error to console for debugging
    console.error('Application error:', error, errorInfo);

    // In production, you could send this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
  }
</script>

<ErrorBoundary onError={handleError}>
  {#if processingSso}
    <!-- SSO code exchange in progress — do not mount router or fire auth guards -->
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">Completing sign-in...</p>
      </div>
    </div>
  {:else if checkingSetup}
    <!-- Show loading state while checking setup -->
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div class="text-center">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  {:else if !setupComplete && router.currentPath !== '/setup'}
    <!-- Redirect to setup if not complete -->
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div class="text-center">
        <p class="text-gray-600 dark:text-gray-400">Redirecting to setup...</p>
      </div>
    </div>
  {:else}
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {#if setupComplete && authManager.isAuthenticated && !PUBLIC_PATHS.has(router.currentPath)}
        <Navigation currentPath={router.currentPath} />
      {/if}
      <main class="flex-1">
        <Router {routes} />
      </main>

      {#if setupComplete && authManager.isAuthenticated && !PUBLIC_PATHS.has(router.currentPath)}
        <!-- Footer -->
        <footer class="mt-auto py-8 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div class="w-full px-4 sm:px-6 lg:px-8 text-left">
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Made by Alessandro Franceschi <a
                href="https://example42.com"
                target="_blank"
                class="text-blue-600 dark:text-blue-400 hover:underline"
              > (example42.com)</a> and his AI assistants
            </p>
          </div>
        </footer>
      {/if}
    </div>
  {/if}

  <!-- Toast notifications -->
  <ToastContainer />
</ErrorBoundary>
